import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { onZefDialogClose, zefDialogClose, zefDialogOpen, selectZefDialogState } from '@zerops/zef/dialog';
import { delay, filter, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { controlPlaneDialogActions, ControlPlaneDialogDefaults } from './control-plane-dialog.state';
import {
  FEATURE_NAME,
  ZCP_RECIPE_SOURCE
} from './control-plane-dialog.constant';
import {
  ControlPlaneForm,
  ControlPlaneFormValue,
  enabledAgentsFromForm,
  getAgentAuthType
} from './control-plane-dialog.form';
import {
  firstClassRecipeImport,
  firstClassRecipeImportSuccess
} from '@zerops/zerops/core/service-stack-base';
import { ProjectEntity } from '@zerops/zerops/core/project-base';
import {
  ZagentAgentType,
  ZagentDialogMode,
  ZagentRecipeContext,
  ControlPlaneMiniConfig,
  modeToDefaults
} from '@zerops/zerops/core/zerops-services';
import { ServiceStackTypeCategories } from '@zerops/models/service-stack';

interface DialogMeta {
  projectId?: string;
  dialogMode?: ZagentDialogMode;
  recipeContext?: ZagentRecipeContext;
  initialConfig?: ControlPlaneMiniConfig;
  defaults?: ControlPlaneDialogDefaults;
}

/**
 * Map an `initialConfig` (recipe-mode dialog reopen) back onto the per-agent
 * form fields. Two cases:
 *
 * 1. **Multi-agent payload (new shape, `agents` is present — including the
 *    empty array case).** Per-agent enables derive directly from the list:
 *    an explicit `agents: []` means CDE-only mode and all four checkboxes
 *    correctly stay false. Mixing in the legacy `agentType` mirror here
 *    would silently re-check whatever the default was, smuggling a removed
 *    agent back in on dialog close.
 * 2. **Legacy single-agent payload (no `agents` field at all, only
 *    `agentType`).** The single agent named in `agentType` becomes the only
 *    enabled agent — its auth/token populate that one slot. Keeps
 *    pre-multi-agent saved configs from quietly resetting to Claude when
 *    reopened.
 */
function perAgentFromInitialConfig(c: ControlPlaneMiniConfig): Pick<
  ControlPlaneFormValue,
  | 'enableAgentClaudeCode' | 'enableAgentCodex' | 'enableAgentAntigravity' | 'enableAgentGrok' | 'enableAgentCursor'
  | 'authTypeClaudeCode' | 'authTypeCodex' | 'authTypeAntigravity' | 'authTypeGrok' | 'authTypeCursor'
  | 'apiTokenClaudeCode' | 'apiTokenCodex' | 'apiTokenAntigravity' | 'apiTokenGrok' | 'apiTokenCursor'
> {
  if (c.agents !== undefined) {
    const find = (t: ZagentAgentType) => c.agents?.find((a) => a.agentType === t);
    const claude = find('claude-code');
    const codex = find('codex');
    const antig = find('antigravity');
    const grok = find('grok');
    const cursor = find('cursor');
    // Legacy `apiToken` on the root is the *primary* agent's token — used as
    // a fallback only for the matching agent and only when its own `apiToken`
    // entry is absent (e.g. pre-Phase-1D saved configs).
    const legacyForPrimary = (t: ZagentAgentType) => c.agentType === t ? (c.apiToken ?? '') : '';
    return {
      enableAgentClaudeCode: !!claude,
      enableAgentCodex: !!codex,
      enableAgentAntigravity: !!antig,
      enableAgentGrok: !!grok,
      enableAgentCursor: !!cursor,
      authTypeClaudeCode: claude?.authType ?? 'oauth',
      authTypeCodex: codex?.authType ?? 'oauth',
      authTypeAntigravity: antig?.authType ?? 'oauth',
      authTypeGrok: grok?.authType ?? 'oauth',
      authTypeCursor: cursor?.authType ?? 'oauth',
      apiTokenClaudeCode: claude?.apiToken ?? legacyForPrimary('claude-code'),
      apiTokenCodex: codex?.apiToken ?? legacyForPrimary('codex'),
      apiTokenAntigravity: antig?.apiToken ?? legacyForPrimary('antigravity'),
      apiTokenGrok: grok?.apiToken ?? legacyForPrimary('grok'),
      apiTokenCursor: cursor?.apiToken ?? legacyForPrimary('cursor')
    };
  }

  // Legacy single-agent payload — `agents` field missing entirely.
  const t: ZagentAgentType | undefined = c.agentType;
  return {
    enableAgentClaudeCode: t === 'claude-code',
    enableAgentCodex: t === 'codex',
    enableAgentAntigravity: t === 'antigravity',
    enableAgentGrok: t === 'grok',
    enableAgentCursor: t === 'cursor',
    authTypeClaudeCode: t === 'claude-code' ? (c.authType ?? 'oauth') : 'oauth',
    authTypeCodex: t === 'codex' ? (c.authType ?? 'oauth') : 'oauth',
    authTypeAntigravity: t === 'antigravity' ? (c.authType ?? 'oauth') : 'oauth',
    authTypeGrok: t === 'grok' ? (c.authType ?? 'oauth') : 'oauth',
    authTypeCursor: t === 'cursor' ? (c.authType ?? 'oauth') : 'oauth',
    apiTokenClaudeCode: t === 'claude-code' ? (c.apiToken ?? '') : '',
    apiTokenCodex: t === 'codex' ? (c.apiToken ?? '') : '',
    apiTokenAntigravity: t === 'antigravity' ? (c.apiToken ?? '') : '',
    apiTokenGrok: t === 'grok' ? (c.apiToken ?? '') : '',
    apiTokenCursor: t === 'cursor' ? (c.apiToken ?? '') : ''
  };
}

@Injectable()
export class ControlPlaneDialogEffect {

  // # Deps
  #actions$ = inject(Actions);
  #store = inject(Store);
  #form = inject(ControlPlaneForm);
  #projectEntity = inject(ProjectEntity);

  // # Streams
  dialogOpen$ = this.#actions$.pipe(
    ofType(zefDialogOpen),
    filter(({ key }) => key === FEATURE_NAME),
    map(({ meta }) => meta as DialogMeta)
  );

  dialogClose$ = this.#actions$.pipe(onZefDialogClose(FEATURE_NAME));

  // # Effects

  /**
   * Initialize form when dialog opens in STANDALONE mode
   * Fetches project entity to get available services
   */
  onDialogOpenStandalone$ = createEffect(() => this.dialogOpen$.pipe(
    filter(({ dialogMode }) => dialogMode !== 'recipe'),
    filter((meta): meta is DialogMeta & { projectId: string } => !!meta.projectId),
    switchMap(({ projectId, defaults }) => this.#projectEntity.entityByIdWithServiceStacks$(projectId).pipe(
      filter((project) => !!project),
      take(1),
      map((project) => {
        // Filter to only USER category services, exclude zcp*
        const services = project?.serviceStacks
          ?.filter((service) =>
            service.activeAppVersion &&
            service.serviceStackTypeInfo?.serviceStackTypeCategory === ServiceStackTypeCategories.User &&
            !service.name.toLowerCase().startsWith('zcp')
          )
          ?.map((service) => ({
            id: service.id,
            hostname: service.name,
            type: service.serviceStackTypeInfo?.serviceStackTypeName
          })) || [];

        const formDefaults = this.#form.getDefaultValues();

        const formValue: ControlPlaneFormValue = {
          ...formDefaults,
          enableAgent: defaults?.enableAgent ?? true,
          enableVscode: defaults?.enableVscode ?? true,
          selectedServices: services.map(() => false)
        };

        return [
          this.#form.setValue(formValue)
        ];
      }),
      mergeMap((actions) => actions)
    ))
  ));

  /**
   * Initialize form when dialog opens in RECIPE mode
   * Uses recipeContext services and initialConfig from meta
   */
  onDialogOpenRecipe$ = createEffect(() => this.dialogOpen$.pipe(
    filter(({ dialogMode }) => dialogMode === 'recipe'),
    map(({ recipeContext, initialConfig, defaults }) => {
      // Filter to only USER category services, exclude zcp*
      const services = recipeContext?.services
        ?.filter((s) => s.category === 'USER' && !s.name.toLowerCase().startsWith('zcp'))
        ?.map((s) => ({
          id: s.name,
          hostname: s.name,
          type: s.typeName
        })) || [];

      // Resolve toggle values from initialConfig with mode-fallback for legacy saves,
      // then caller defaults, then `true`/`true`.
      const legacyFromMode = modeToDefaults(initialConfig?.mode);
      const resolvedEnableAgent =
        initialConfig?.enableAgent
        ?? (initialConfig?.mode !== undefined ? legacyFromMode.enableAgent : undefined)
        ?? defaults?.enableAgent
        ?? true;
      const resolvedEnableVscode =
        initialConfig?.enableVscode
        ?? initialConfig?.includeVscode
        ?? (initialConfig?.mode !== undefined ? legacyFromMode.enableVscode : undefined)
        ?? defaults?.enableVscode
        ?? true;

      const formDefaults = this.#form.getDefaultValues();

      // If we have initial config, derive per-agent fields from its single-agent shape
      if (initialConfig) {
        const hasExplicitSelection = initialConfig.selectedServices?.length > 0;
        const selectedServicesArr = services.map((s) =>
          hasExplicitSelection
            ? initialConfig.selectedServices.includes(s.hostname)
            : s.hostname.toLowerCase().includes('dev')
        );

        const formValue: ControlPlaneFormValue = {
          ...formDefaults,
          ...perAgentFromInitialConfig(initialConfig),
          enableAgent: resolvedEnableAgent,
          enableVscode: resolvedEnableVscode,
          enablePublicAccess: initialConfig.enablePublicAccess ?? true,
          vscodePassword: initialConfig.vscodePassword ?? this.#form.generateNewPassword(),
          selectedServices: selectedServicesArr
        };

        return [
          this.#form.setValue(formValue)
        ];
      }

      // No initial config - set defaults for recipe mode, pre-select dev-suffixed services
      const selectedServicesArr = services.map((s) =>
        s.hostname.toLowerCase().includes('dev')
      );

      const formValue: ControlPlaneFormValue = {
        ...formDefaults,
        enableAgent: resolvedEnableAgent,
        enableVscode: resolvedEnableVscode,
        selectedServices: selectedServicesArr
      };

      return [
        this.#form.setValue(formValue)
      ];
    }),
    mergeMap((actions) => actions)
  ));

  // Deploy using first-class recipe endpoint
  onDeploy$ = createEffect(() => this.#actions$.pipe(
    ofType(controlPlaneDialogActions.deploy),
    withLatestFrom(this.#form.value$),
    map(([ action, formValue ]) => {
      // Honor the master "Include Coding Agents" toggle: when it is off the
      // per-agent booleans are only hidden (not reset), so derive an empty
      // agent set rather than seeding the auth walker for an uninstalled agent.
      const enabledAgents = formValue.enableAgent ? enabledAgentsFromForm(formValue) : [];
      const primary = enabledAgents[0];
      const primaryAuthType = primary ? getAgentAuthType(formValue, primary) : formValue.authType;
      // OAuth-mode agents from this deploy — the auth-dialog walker uses
      // this to seed its queue without round-tripping through userData.
      const oauthAgents = enabledAgents.filter((t) => getAgentAuthType(formValue, t) === 'oauth');
      return firstClassRecipeImport({
        data: {
          projectId: action.projectId,
          serviceImportYaml: action.yamlContent,
          recipeSource: ZCP_RECIPE_SOURCE,
          createIntegrationToken: action.createIntegrationToken,
          zcpAgents: oauthAgents
        },
        meta: { source: FEATURE_NAME, authType: primaryAuthType, agentType: primary ?? formValue.agentType }
      });
    })
  ));

  // When first-class recipe import succeeds, close the dialog
  onDeploySuccess$ = createEffect(() => this.#actions$.pipe(
    ofType(firstClassRecipeImportSuccess),
    filter(({ originalAction }) => originalAction?.meta?.source === FEATURE_NAME),
    withLatestFrom(this.#store.pipe(select(selectZefDialogState(FEATURE_NAME)))),
    map(([ _, dialogState ]) => zefDialogClose({ key: FEATURE_NAME, meta: dialogState?.meta }))
  ));

  // Reset form when dialog closes
  onDialogCloseResetForm$ = createEffect(() => this.dialogClose$.pipe(
    delay(200),
    mergeMap(() => [
      this.#form.reset(),
      this.#form.setDefaultValues()
    ])
  ));
}
